import type { Coordinate, DurationTimeInMillis, GooglePlaceResult, Meters, TimeIntervalInMillis, TimestampInMillis, UUID } from '@cobliteam/common-types';
import type { GoogleGeocoderResult } from '@cobliteam/common-types/src/google';

import type { RoutingSpreadsheetNoPermissionMessage } from '@cobliteam/routing/src/permissions';

import type { VehicleDto } from '../../../__generated__/graphql';
import type { Driver } from '../../driver';
import type { ProofOfConclusion } from '../../proofOfConclusion';

export interface PlannedVsExecuted<T> {
  planned: T;
  executed?: T;
}

export enum ActivityRouteRelation {
  ACTIVITIES_WITHIN_ROUTE = 'ACTIVITIES_WITHIN_ROUTE',
  ACTIVITIES_WITHOUT_ROUTE = 'ACTIVITIES_WITHOUT_ROUTE',
  ALL_ACTIVITIES = 'ALL_ACTIVITIES',
}

export enum NewRoutingReportType {
  ROUTES = 'ROUTES',
  ROUTE = 'ROUTE',
  ACTIVITY = 'ACTIVITIES',
}

export enum GeocoderAddressComponentTypes {
  CITY = 'administrative_area_level_2',
  COUNTRY = 'country',
  STREET_COMPLEMENT = 'subpremise',
  NEIGHBORHOOD = 'sublocality_level_1',
  POSTAL_CODE = 'postal_code',
  STATE = 'administrative_area_level_1',
  STREET_ADDRESS = 'route',
  STREET_NUMBER = 'street_number',
}

export enum NewActivityType {
  START = 'START',
  END = 'END',
  STOP = 'STOP',
}

export enum ActivityVisitStatus {
  PENDING = 'PENDING',
  ON_SITE = 'ON_SITE',
  VISITED = 'VISITED',
  NOT_VISITED = 'NOT_VISITED',
}

export enum ActivityCompletionStatus {
  PARTIALLY_COMPLETED = 'PARTIALLY_COMPLETED',
  NOT_COMPLETED = 'NOT_COMPLETED',
  COMPLETED = 'COMPLETED',
}

export enum ActivityCompletionStatusFilter {
  NOT_COMPLETED = 'NOT_COMPLETED',
  COMPLETED = 'COMPLETED',
  PLANNED = 'PLANNED',
}

export enum RouteType {
  MANUAL = 'MANUAL',
  OPTIMIZED = 'OPTIMIZED',
  ACTIVITY = 'ACTIVITY',
}

export enum RouteStatus {
  PLANNED = 'PLANNED',
  DELAYED = 'DELAYED',
  IN_PROGRESS = 'IN_PROGRESS',
  PARTIALLY_EXECUTED = 'PARTIALLY_EXECUTED',
  EXECUTED = 'EXECUTED',
  UNEXECUTED = 'UNEXECUTED',
}

export enum RoutingObjective {
  MIN_COMPLETION_TIME = 'MIN_COMPLETION_TIME',
  MIN_TRANSPORT_TIME = 'MIN_TRANSPORT_TIME',
  MIN_VEHICLES = 'MIN_VEHICLES',
}

export enum ActivitiesSpreadsheetFieldType {
  ADDRESS = 'ADDRESS',
  NAME = 'NAME',
  PHONE_NUMBER = 'PHONE_NUMBER',
  ADDITIONAL_INFO = 'ADDITIONAL_INFO',
  TIME_WINDOW = 'TIME_WINDOW',
  LOAD_SIZE = 'LOAD_SIZE',
  START_HOUR = 'START_HOUR',
  START_DATE = 'START_DATE',
}

export enum RouteRecurrencyFrequency {
  NONE = 'NONE',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export enum PropagationType {
  ALL_ROUTES = 'ALL_ROUTES',
  ONLY_THIS_ROUTE = 'ONLY_THIS_ROUTE',
}

export enum PlanningType {
  MANUAL_ROUTING = 'MANUAL_ROUTING',
  OPTIMIZED_ROUTING = 'OPTIMIZED_ROUTING',
  ACTIVITIES_WITHOUT_ROUTE = 'ACTIVITIES_WITHOUT_ROUTE',
}

// Routing settings
export interface RouteWorkingHours {
  startTime: TimestampInMillis;
  endTime: TimestampInMillis;
}

export interface RouteBreak {
  startTime: TimestampInMillis;
  endTime: TimestampInMillis;
  duration: TimeIntervalInMillis;
}

export interface RoutingOptimization {
  workingHours: RouteWorkingHours;
  numVehicles: number;
  objectives?: RoutingObjective[];
  endRouteDestination?: InputDestination;
  break?: RouteBreak;
  vehicleAverageCargoCapacity?: number;
}

export interface InputDestination {
  coordinate: Coordinate;
  city?: string;
  country?: string;
  neighborhood?: string;
  postalCode?: string;
  state?: string;
  streetAddress?: string;
  streetComplement?: string;
  streetNumber?: string;
}

export interface TimeWindow {
  earliest: TimestampInMillis;
  latest: TimestampInMillis;
}

export interface InputActivity {
  id?: UUID;
  destination: GooglePlaceResult;
  loadSize?: number;
  duration?: TimeIntervalInMillis;
  name?: string;
  startTime?: TimestampInMillis;
  startTimeWindow?: string;
  endTimeWindow?: string;
  additionalInfo?: string;
  visitStatus?: ActivityVisitStatus;
  completionStatus?: ActivityCompletionStatus;
  phoneNumber?: string;
  startHour?: string;
  startDate?: TimeIntervalInMillis;
  driverId?: UUID;
  deviceId?: string;
  driverName?: string;
  vehicleId?: UUID;
}

export interface RoutingSettings {
  activities: InputActivity[];
  startTime: TimestampInMillis;
  type: RouteType;
  optimization?: RoutingOptimization;
}

// Route
export interface NewDestination extends InputDestination {}

export interface NewActivity {
  id: UUID;
  type: NewActivityType;
  visitStatus: ActivityVisitStatus;
  destination: NewDestination;
  createdAt: TimestampInMillis;
  updatedAt: TimestampInMillis;
  driverId?: UUID;
  driverName?: string;
  position?: number;
  deviceId?: string;
  vehicleId?: string;
  routeId?: UUID;
  routeName?: UUID;
  name?: string;
  plannedDuration?: TimeIntervalInMillis;
  plannedDistanceInMeters?: number;
  startTime?: TimestampInMillis;
  endTime?: TimestampInMillis;
  timeWindows?: TimeWindow[];
  waitingTime?: TimeIntervalInMillis;
  additionalInfo?: string;
  executedDuration?: TimeIntervalInMillis;
  arrivalTime?: TimestampInMillis;
  proofOfConclusion?: ProofOfConclusion;
  completionStatus?: ActivityCompletionStatus;
  executedEndTime?: TimestampInMillis;
  phoneNumber?: string;
  loadSize?: number;
}

export interface RouteSettings {
  routingType: RouteType;
  objectives: RoutingObjective[];
  workingHours: RouteWorkingHours;
  breakWindow?: RouteBreak;
  vehicleAverageCargoCapacity?: number;
}

export interface NewRoute {
  id: UUID;
  name: string;
  activities: NewActivity[];
  type: RouteType;
  status: RouteStatus;
  plannedDuration: TimeIntervalInMillis;
  plannedDistanceInMeters: number;
  createdAt: TimestampInMillis;
  updatedAt: TimestampInMillis;
  startTime: TimestampInMillis;
  executedStartTime?: TimestampInMillis;
  points?: Coordinate[];
  endTime?: TimestampInMillis;
  executedEndTime?: TimestampInMillis;
  vehicleId?: UUID;
  driverId?: UUID;
  deviceId?: string;
  sharedId?: UUID;
  driverName?: string;
  break?: RouteBreak;
  settings?: RouteSettings;
  recurrency?: RouteRecurrency;
  propagationType?: PropagationType;
}

export interface ValidatedSpreadsheet {
  activities: InputActivity[];
  invalidAddresses: string[];
}

export interface EditActivity extends InputActivity {
  type: NewActivityType;
  id?: UUID;
  position?: number;
}

export interface EditRoute {
  id: UUID;
  name: string;
  startTime: TimestampInMillis;
  activities: EditActivity[];
  settings: RouteSettings;
  driverId?: UUID;
  deviceId?: string;
  driverName?: string;
  vehicleId?: UUID;
  recurrency?: RouteRecurrency;
  propagationType?: PropagationType;
}

export interface ParsedSpreadsheetTab {
  name: string;
  data: string[][];
}

export interface ActivitiesSpreadsheetImportFormValues {
  spreadsheetFile?: File;
  selectedFieldColumnsIndexes: Record<ActivitiesSpreadsheetFieldType, number[]>;
  selectedHeaderIndex?: number;
  selectedTab?: ParsedSpreadsheetTab;
}

export interface GeocodedAddress {
  address: GoogleGeocoderResult[];
  coordinate: Coordinate;
}

export interface ReportActivity {
  id: UUID;
  type: NewActivityType;
  startTime: PlannedVsExecuted<TimestampInMillis>;
  endTime: PlannedVsExecuted<TimestampInMillis>;
  destination: NewDestination;
  instructions?: string;
  observations?: string | RoutingSpreadsheetNoPermissionMessage;
  timeWindows?: TimeWindow[];
  duration: PlannedVsExecuted<DurationTimeInMillis>;
  name?: string;
  completionStatus?: ActivityCompletionStatus;
  phoneNumber?: string;
  driver?: Driver;
  vehicle?: VehicleDto;
}

export interface NewRouteReport {
  id?: UUID;
  status?: RouteStatus;
  activities: ReportActivity[];
  distance?: PlannedVsExecuted<Meters | RoutingSpreadsheetNoPermissionMessage>;
  duration?: PlannedVsExecuted<DurationTimeInMillis>;
  startTime?: PlannedVsExecuted<TimestampInMillis>;
  endTime?: PlannedVsExecuted<TimestampInMillis>;
  name?: string;
  driver?: Driver;
  vehicle?: VehicleDto;
}

export interface ExecutionDigest {
  total: number;
  ids: UUID[];
}

export interface ActivitiesSummary {
  total: number;
  completed: ExecutionDigest;
  notCompleted: ExecutionDigest;
  waiting: ExecutionDigest;
}

export interface RoutesSummary {
  total: number;
  routesWithVehicle: ExecutionDigest;
  routesWithoutVehicle: ExecutionDigest;
}

export interface OperationSummary {
  routes: RoutesSummary;
  activities: ActivitiesSummary;
}

export interface RouteRecurrency {
  id?: UUID;
  repeatInterval: number;
  frequency: RouteRecurrencyFrequency;
  daysOfMonth?: number[];
  weekNumbers?: number[];
  daysOfWeek?: number[];
  untilDate?: TimestampInMillis;
}

export interface DeleteRoutes {
  ids: UUID[];
  propagationType?: PropagationType;
}

export interface ActivityVehicleAssociation {
  id: UUID;
  deviceId?: string;
  vehicleId?: UUID;
  driverId?: UUID;
  driverName?: string;
}
